import dayjs from "dayjs";
import { ToWords } from "to-words/en-IN";

const toWords = new ToWords();

/** One line item on the salary slip earnings or deductions table. */
export type PayslipAmountRow = {
  id: string;
  type: string;
  label: string;
  /** Absolute INR amount for display. */
  amount: number;
};

export type PayslipComponentLike = {
  id: string;
  type: string;
  label: string;
  amount: number;
};

export type BuildPayslipTablesInput = {
  components?: PayslipComponentLike[] | null;
  earningsAmount: number;
  deductionsAmount: number;
  /** When set, LOP rows are labeled with day count, e.g. `Loss of pay (2 days)`. */
  lopDays?: number | null;
};

export type PayslipTables = {
  earnings: PayslipAmountRow[];
  deductions: PayslipAmountRow[];
  totalEarnings: number;
  totalDeductions: number;
};

/**
 * Wage month label for salary slips, e.g. `2026-09` → `Sep-2026`.
 * Returns the raw value when not a valid YYYY-MM.
 */
export function formatPayslipWageMonth(yearMonth: string): string {
  const trimmed = typeof yearMonth === "string" ? yearMonth.trim() : "";
  if (!/^\d{4}-\d{2}$/.test(trimmed)) return trimmed || "—";
  const d = dayjs(`${trimmed}-01`);
  if (!d.isValid() || d.format("YYYY-MM") !== trimmed) return trimmed;
  return d.format("MMM-YYYY");
}

/** Indian English amount in words for salary slips, e.g. `Thirty Thousand Only`. */
export function formatPayslipAmountInWords(amount: number): string {
  const n = Number.isFinite(amount) ? Math.round(Math.abs(amount)) : 0;
  return `${toWords.convert(n)} Only`;
}

function formatLopLabel(lopDays: number | null | undefined): string {
  if (lopDays == null || !Number.isFinite(lopDays)) {
    return "Loss of pay";
  }
  const days = lopDays;
  const unit = days === 1 ? "day" : "days";
  return `Loss of pay (${days} ${unit})`;
}

function sumAmounts(rows: PayslipAmountRow[]): number {
  return rows.reduce((sum, row) => sum + row.amount, 0);
}

/**
 * Split payroll line components into earnings vs deductions tables.
 * Amounts are absolute for display; falls back to line totals when components are empty.
 */
export function buildPayslipTables(input: BuildPayslipTablesInput): PayslipTables {
  const components = input.components ?? [];
  const lopLabel = formatLopLabel(input.lopDays);

  const earningsFromComponents = components
    .filter((c) => c.type === "EARNINGS" || c.amount > 0)
    .map((c) => ({
      id: c.id,
      type: c.type,
      label: c.label,
      amount: Math.abs(c.amount),
    }));

  const deductionsFromComponents = components
    .filter((c) => c.type !== "EARNINGS" && c.amount <= 0)
    .map((c) => ({
      id: c.id,
      type: c.type,
      label: c.type === "LOP" ? lopLabel : c.label,
      amount: Math.abs(c.amount),
    }));

  const earnings =
    earningsFromComponents.length > 0
      ? earningsFromComponents
      : [
          {
            id: "earnings-fallback",
            type: "EARNINGS",
            label: "Gross earnings",
            amount: Math.max(0, Number(input.earningsAmount) || 0),
          },
        ];

  const deductions =
    deductionsFromComponents.length > 0
      ? deductionsFromComponents
      : Number(input.deductionsAmount) > 0
        ? [
            {
              id: "deductions-fallback",
              type: "OTHER",
              label: "Deductions",
              amount: Math.abs(Number(input.deductionsAmount) || 0),
            },
          ]
        : [];

  return {
    earnings,
    deductions,
    totalEarnings: sumAmounts(earnings),
    totalDeductions: sumAmounts(deductions),
  };
}
